import { getEmptyStarterBlockIds } from '@affine/core/modules/doc/services/docs';
import { correctNumberedListsOrderToPrev } from '@blocksuite/affine/blocks/list';
import {
  ListBlockModel,
  NoteBlockModel,
  NoteDisplayMode,
  ParagraphBlockModel,
} from '@blocksuite/affine/model';
import { replaceIdMiddleware } from '@blocksuite/affine/shared/adapters';
import { matchModels } from '@blocksuite/affine/shared/utils';
import {
  type BlockModel,
  type BlockSnapshot,
  type DeltaOperation,
  Slice,
  type SliceSnapshot,
  type Store,
  type Text,
} from '@blocksuite/affine/store';

/** Where `/template` was typed: a block and a text offset inside it. */
export type TemplateCaret = { blockId: string; index: number };

export type TemplateInsertResult = {
  /** Where the caret should go afterwards, or null to leave it alone. */
  focus: TemplateCaret | null;
};

/**
 * The blocks a template contributes: the children of every note shown in
 * page mode, in order. The title, the canvas and edgeless-only notes stay
 * behind.
 */
export function getTemplateContentBlocks(store: Store): BlockModel[] {
  return (store.root?.children ?? [])
    .filter(
      (child): child is NoteBlockModel =>
        matchModels(child, [NoteBlockModel]) &&
        child.props.displayMode !== NoteDisplayMode.EdgelessOnly
    )
    .flatMap(note => note.children);
}

/**
 * Snapshots a template's content for {@link insertTemplateSnapshot}, or
 * returns null when it has nothing worth inserting.
 *
 * The result is plain JSON with no ties to the template doc, so it stays
 * valid after the template is released.
 */
export function snapshotTemplateContent(store: Store): SliceSnapshot | null {
  const blocks = getTemplateContentBlocks(store);
  if (blocks.length === 0 || getEmptyStarterBlockIds(blocks).length > 0) {
    return null;
  }
  const snapshot = store
    .getTransformer()
    .sliceToSnapshot(Slice.fromModels(store, blocks));
  if (!snapshot) {
    throw new Error(`Failed to snapshot template ${store.doc.id}`);
  }
  // Delta attribute values are shared with the template's Yjs data; a JSON
  // round trip detaches them before anything below edits the snapshot.
  const detached = JSON.parse(JSON.stringify(snapshot)) as SliceSnapshot;
  detached.content.forEach(block => normalizeBlock(block, store.doc.id));
  return detached;
}

function normalizeBlock(block: BlockSnapshot, templateDocId: string) {
  if (block.flavour === 'affine:surface-ref') {
    // The frame or group it shows lives on the template's canvas, which is
    // not copied. Link to it instead, as pasting into another doc does
    // (surfaceRefToEmbed). Done here rather than by that middleware so the
    // schema check in insertTemplateSnapshot sees the flavour that lands.
    block.flavour = 'affine:embed-linked-doc';
    block.props = {
      pageId: templateDocId,
      params: { mode: 'page', blockIds: [block.id] },
    };
    block.children = [];
    return;
  }

  // Author and timestamps belong to the template; `repeat` would enrol the
  // copy in the template's /repeat series; comment anchors would point the
  // copy at the template's comment threads.
  for (const key of Object.keys(block.props)) {
    if (key === 'repeat' || key === 'comments' || key.startsWith('meta:')) {
      delete block.props[key];
    }
  }
  getDelta(block)?.forEach(op => {
    if (!op.attributes) return;
    for (const key of Object.keys(op.attributes)) {
      if (key.startsWith('comment-')) {
        delete (op.attributes as Record<string, unknown>)[key];
      }
    }
  });

  block.children.forEach(child => normalizeBlock(child, templateDocId));
}

function getDelta(block: BlockSnapshot): DeltaOperation[] | null {
  const text = block.props.text as { delta?: unknown } | undefined;
  return text && Array.isArray(text.delta)
    ? (text.delta as DeltaOperation[])
    : null;
}

/** A lone plain paragraph is spliced into the line like typed text. */
function getSnippetDelta(snapshot: SliceSnapshot) {
  if (snapshot.content.length !== 1) return null;
  const [block] = snapshot.content;
  if (
    block.flavour !== 'affine:paragraph' ||
    block.props.type !== 'text' ||
    block.children.length > 0
  ) {
    return null;
  }
  return getDelta(block);
}

function deltaLength(delta: DeltaOperation[]) {
  return delta.reduce(
    (length, op) =>
      length + (typeof op.insert === 'string' ? op.insert.length : 0),
    0
  );
}

function isTextLine(model: BlockModel) {
  return matchModels(model, [ParagraphBlockModel, ListBlockModel]);
}

function isEmptyLine(model: BlockModel) {
  return (model.text?.length ?? 0) === 0 && model.children.length === 0;
}

/**
 * Finds the level to insert at: the caret line's parent when it can hold
 * every top-level block, otherwise the nearest ancestor that can, inserting
 * after the block that contains the line (`anchor`).
 *
 * The walk stops at the note. Above it are the root and the canvas, which
 * accept dividers, images and embeds through schema wildcards. Outside a
 * note (edgeless text) only the direct parent is tried.
 */
function resolveInsertionPoint(
  store: Store,
  line: BlockModel,
  flavours: string[]
) {
  const ancestors: BlockModel[] = [];
  for (
    let parent = store.getParent(line);
    parent;
    parent = store.getParent(parent)
  ) {
    ancestors.push(parent);
    if (matchModels(parent, [NoteBlockModel])) break;
  }
  const insideNote = ancestors.some(ancestor =>
    matchModels(ancestor, [NoteBlockModel])
  );
  const candidates = insideNote ? ancestors : ancestors.slice(0, 1);

  let anchor = line;
  for (const parent of candidates) {
    if (
      flavours.every(flavour =>
        store.schema.safeValidate(flavour, parent.flavour)
      )
    ) {
      return { parent, anchor };
    }
    anchor = parent;
  }
  return null;
}

/**
 * Moves the text after `index` into a new line placed after `after`, the
 * way Enter splits a paragraph: same flavour and type, formatting kept, and
 * the line's children go with the second half.
 */
function splitLine(
  store: Store,
  line: BlockModel,
  index: number,
  after: BlockModel
) {
  const text = line.text;
  const parent = store.getParent(line);
  // The line may have been edited while the template was being inserted.
  if (!text || !parent || index <= 0 || index >= text.length) return null;

  let right: Text;
  try {
    right = text.split(index);
  } catch (error) {
    // Non-string inserts cannot be split; the template stays after the line.
    console.error(error);
    return null;
  }
  const tailId = store.addBlock(
    line.flavour,
    { text: right, type: (line.props as { type?: string }).type },
    parent,
    parent.children.indexOf(after) + 1
  );
  const tail = store.getModelById(tailId);
  if (tail && line.children.length > 0) {
    store.moveBlocks(line.children, tail);
  }
  return tail;
}

/**
 * Inserts a {@link snapshotTemplateContent} snapshot at the caret:
 *
 * - a lone plain paragraph is spliced into the line at the caret;
 * - otherwise the blocks replace an empty line, or go before the line (caret
 *   at its start), after it (caret at its end), or between its two halves
 *   (caret in the middle);
 * - if the line's container cannot hold the blocks, they go after the
 *   enclosing block at the nearest level that can, and the line is not
 *   split.
 *
 * Every call gets fresh block ids, so the same template can go into the same
 * doc any number of times. Returns null when nothing could be inserted.
 */
export async function insertTemplateSnapshot(
  store: Store,
  snapshot: SliceSnapshot,
  caret: TemplateCaret
): Promise<TemplateInsertResult | null> {
  const line = store.getModelById(caret.blockId);
  if (!line || store.readonly || snapshot.content.length === 0) return null;
  const index = Math.min(Math.max(caret.index, 0), line.text?.length ?? 0);

  const snippet = getSnippetDelta(snapshot);
  if (snippet && line.text) {
    store.captureSync();
    line.text.applyDelta(index > 0 ? [{ retain: index }, ...snippet] : snippet);
    store.captureSync();
    return {
      focus: { blockId: line.id, index: index + deltaLength(snippet) },
    };
  }

  const point = resolveInsertionPoint(
    store,
    line,
    snapshot.content.map(block => block.flavour)
  );
  if (!point) return null;
  const { parent, anchor } = point;
  const hoisted = anchor !== line;
  const textLength = line.text?.length ?? 0;

  let insertAt = parent.children.indexOf(anchor) + 1;
  let replacesLine = false;
  let splitAt: number | null = null;
  if (!hoisted) {
    if (isEmptyLine(line)) {
      insertAt -= 1;
      replacesLine = true;
    } else if (textLength > 0 && index === 0) {
      insertAt -= 1;
    } else if (isTextLine(line) && index > 0 && index < textLength) {
      splitAt = index;
    }
  }

  store.captureSync();
  // A fresh transformer per insertion: replaceIdMiddleware keeps its id map
  // on the instance, and a reused map would hand out the same ids again.
  const slice = await store
    .getTransformer([replaceIdMiddleware(store.workspace.idGenerator)])
    .snapshotToSlice(snapshot, store, parent.id, insertAt);
  // snapshotToSlice logs and swallows its own errors.
  const inserted = (slice?.content ?? [])
    .map(block => store.getModelById(block.id))
    .filter((model): model is BlockModel => !!model);
  const last = inserted.at(-1);
  if (!last) return null;

  const tail = splitAt === null ? null : splitLine(store, line, splitAt, last);

  // Drop the emptied command line only when the caret has somewhere to go,
  // and never leave a container (a rich-table cell, say) with no children.
  const lastIsText = isTextLine(last);
  if (
    lastIsText &&
    store.hasBlock(line.id) &&
    isEmptyLine(line) &&
    (hoisted ? (store.getParent(line)?.children.length ?? 0) > 1 : replacesLine)
  ) {
    store.deleteBlock(line);
  }

  correctNumberedListsOrderToPrev(store, inserted[0]);
  const next = store.getNext(last);
  if (next) correctNumberedListsOrderToPrev(store, next);
  store.captureSync();

  let focus: TemplateCaret | null = null;
  if (lastIsText) {
    focus = { blockId: last.id, index: last.text?.length ?? 0 };
  } else if (tail) {
    focus = { blockId: tail.id, index: 0 };
  } else if (store.hasBlock(line.id)) {
    focus = {
      blockId: line.id,
      index: Math.min(index, line.text?.length ?? 0),
    };
  }
  return { focus };
}
